import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, XCircle, Search, Info } from 'lucide-react';
import { useAwardShortlistSuggestion, AwardShortlistBook } from '@/hooks/useAwardShortlistSuggestion';
import { toast } from '@/hooks/use-toast';
import { BookSelector } from '@/components/BookSelector';

const PRIZE_OPTIONS = [
  'The Booker Prize',
  'Women\'s Prize for Fiction',
  'Baillie Gifford Prize',
  'International Booker Prize',
  'Pulitzer Prize for Fiction',
  'National Book Awards',
  'Dylan Thomas Prize',
  'Goldsmiths Prize',
  'Scotiabank Giller Prize',
  'Nobel Prize in Literature',
];

interface Props {
  onApprove: (prizeName: string, year: number, books: AwardShortlistBook[]) => void;
}

export function AIAwardShortlistSuggestion({ onApprove }: Props) {
  const [prizeName, setPrizeName] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [suggestions, setSuggestions] = useState<AwardShortlistBook[]>([]);
  const [bookSelectorOpen, setBookSelectorOpen] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  
  const { suggestShortlist, loading } = useAwardShortlistSuggestion();

  const handleGetSuggestions = async () => {
    if (!prizeName || !year) {
      toast({
        title: "Missing information",
        description: "Please select a prize and year",
        variant: "destructive",
      });
      return;
    }

    try {
      const results = await suggestShortlist(prizeName, parseInt(year));
      setSuggestions(results);
      
      const matchedCount = results.filter(b => b.matchConfidence !== 'low').length;
      toast({
        title: "AI Suggestions Ready",
        description: `Found ${results.length} books, ${matchedCount} matched in database`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to get AI suggestions",
        variant: "destructive",
      });
    }
  };

  const handleReplaceBook = (index: number) => {
    setReplacingIndex(index);
    setBookSelectorOpen(true);
  };

  const handleBookSelected = (book: any) => {
    if (replacingIndex !== null && book) {
      const updated = [...suggestions];
      updated[replacingIndex] = {
        ...updated[replacingIndex],
        bookId: book.id,
        title: book.title,
        author: book.authors?.[0] || book.author,
        coverUrl: book.cover_url,
        matchConfidence: 'high',
      };
      setSuggestions(updated);
      setReplacingIndex(null);
    }
    setBookSelectorOpen(false);
  };

  const handleRemoveBook = (index: number) => {
    setSuggestions(suggestions.filter((_, i) => i !== index));
  };

  const handleApprove = () => {
    if (suggestions.length === 0) {
      toast({
        title: "No books to approve",
        description: "Get AI suggestions first",
        variant: "destructive",
      });
      return;
    }

    const unmatchedBooks = suggestions.filter(b => !b.bookId);
    if (unmatchedBooks.length > 0) {
      toast({
        title: "Some books not matched",
        description: `${unmatchedBooks.length} book(s) need to be replaced or removed`,
        variant: "destructive",
      });
      return;
    }

    onApprove(prizeName, parseInt(year), suggestions);
    setSuggestions([]);
    setPrizeName('');
    setYear(new Date().getFullYear().toString());
  };

  const getConfidenceIcon = (confidence?: 'high' | 'medium' | 'low') => {
    if (!confidence || confidence === 'low') return <XCircle className="w-4 h-4 text-destructive" />;
    if (confidence === 'medium') return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    return <CheckCircle2 className="w-4 h-4 text-green-500" />;
  };

  const getConfidenceText = (confidence?: 'high' | 'medium' | 'low') => {
    if (!confidence || confidence === 'low') return 'No match - please search';
    if (confidence === 'medium') return 'Medium confidence - verify';
    return 'High confidence';
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          AI-Powered Shortlist Assistant
        </CardTitle>
        <CardDescription>
          Quickly populate award shortlists using AI. Works for most major awards.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="ai-prize">Prize Name</Label>
            <Select value={prizeName} onValueChange={setPrizeName}>
              <SelectTrigger>
                <SelectValue placeholder="Select a prize" />
              </SelectTrigger>
              <SelectContent>
                {PRIZE_OPTIONS.map(prize => (
                  <SelectItem key={prize} value={prize}>
                    {prize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ai-year">Year</Label>
            <Input
              id="ai-year"
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="2024"
            />
          </div>
        </div>

        <Button
          onClick={handleGetSuggestions}
          disabled={loading || !prizeName || !year}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Getting AI Suggestions...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Get AI Suggestions
            </>
          )}
        </Button>

        {suggestions.length > 0 && (
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">AI Suggestions ({suggestions.length})</h4>
              <Button
                onClick={handleApprove}
                size="sm"
                disabled={suggestions.some(b => !b.bookId)}
              >
                Approve & Use These Books
              </Button>
            </div>

            <div className="grid gap-3">
              {suggestions.map((book, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-muted-foreground">
                      {book.position === 1 ? '🏆' : `${book.position}.`}
                    </span>
                    {getConfidenceIcon(book.matchConfidence)}
                  </div>
                  
                  {book.coverUrl && (
                    <img
                      src={book.coverUrl}
                      alt={book.title}
                      className="w-12 h-16 object-cover rounded"
                    />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{book.title}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {book.author}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {getConfidenceText(book.matchConfidence)}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReplaceBook(index)}
                    >
                      <Search className="w-3 h-3 mr-1" />
                      Replace
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveBook(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <BookSelector 
          open={bookSelectorOpen}
          onClose={() => {
            setBookSelectorOpen(false);
            setReplacingIndex(null);
          }}
          onSelect={handleBookSelected}
          startWithSearch={true}
        />
      </CardContent>
    </Card>
  );
}